import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import type { LucideIcon } from "lucide-react";
import {
  ArrowDown,
  ArrowRight,
  CalendarClock,
  Check,
  Gauge,
  Globe,
  Images,
  Layers,
  RotateCcw,
  Server,
  Sparkles,
  Timer,
  UploadCloud,
} from "lucide-react";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { PreviewsFirstAnimation } from "@/components/webUploader/PreviewsFirstAnimation";
import { FasterThanFtpAnimation } from "@/components/webUploader/FasterThanFtpAnimation";
import { UploadEstimator } from "@/components/webUploader/UploadEstimator";
import { PREVIEW_FIRST_PATH } from "@/content/previewFirst";
import { ON_DEMAND_FTP_HELP_URL, UPLOADER_URL, WEB_UPLOADER_PATH, useWebUploaderCopy } from "@/content/webUploader";
import { soraOnly, useSoraHeadings } from "@/lib/siteFont";
import desktop1x from "@/assets/web-uploader/uploader-desktop-1x.webp";
import desktop2x from "@/assets/web-uploader/uploader-desktop-2x.webp";
import phone1x from "@/assets/web-uploader/uploader-phone-1x.webp";
import phone2x from "@/assets/web-uploader/uploader-phone-2x.webp";
import status1x from "@/assets/web-uploader/status-1x.webp";
import status2x from "@/assets/web-uploader/status-2x.webp";
import queue1x from "@/assets/web-uploader/queue-dock-1x.webp";
import queue2x from "@/assets/web-uploader/queue-dock-2x.webp";
import visitors1x from "@/assets/web-uploader/live-visitors-1x.webp";
import visitors2x from "@/assets/web-uploader/live-visitors-2x.webp";
import resume1x from "@/assets/web-uploader/resume-1x.webp";
import resume2x from "@/assets/web-uploader/resume-2x.webp";
import crumbs1x from "@/assets/web-uploader/breadcrumbs-phone-1x.webp";
import crumbs2x from "@/assets/web-uploader/breadcrumbs-phone-2x.webp";

// "The new Photohawk web uploader" at photohawk.com/web-uploader: the public launch page (17 Oct 2026),
// indexable, linked from the launch email, the uploader's "New" pill, the footer and /preview-first-uploads.
// Copy (and the only claims it may make): src/content/webUploader/copy.<lang>.ts. Screenshots are the real
// uploader with demo data and real CC0 / public-domain running-event photos (WebP, 1x/2x; credits in
// src/assets/web-uploader/CREDITS.md); the two animations are CSS/SVG (src/components/webUploader).

const CANONICAL = `https://photohawk.com${WEB_UPLOADER_PATH}`;

interface Shot { src: string; srcSet: string; width: number; height: number }
const shot = (x1: string, x2: string, width: number, height: number): Shot => ({ src: x1, srcSet: `${x1} 1x, ${x2} 2x`, width, height });

const FEATURE_SHOTS: Record<string, Shot & { phone?: boolean }> = {
  status: shot(status1x, status2x, 399, 330),
  queue: shot(queue1x, queue2x, 960, 600),
  visitors: shot(visitors1x, visitors2x, 720, 230),
  resume: shot(resume1x, resume2x, 960, 330),
  phone: { ...shot(crumbs1x, crumbs2x, 390, 600), phone: true },
};

const POINT_ICONS: Record<string, LucideIcon> = { parallel: Layers, adaptive: Gauge, scale: Images, live: Globe, resume: RotateCcw };
const FTP_ICONS: Record<string, LucideIcon> = { closes: CalendarClock, standard: UploadCloud, ondemand: Server };

const UploaderButton = ({ children, variant = "hero" }: { children: React.ReactNode; variant?: "hero" | "default" }) => (
  <Button variant={variant} size="lg" asChild>
    <a href={UPLOADER_URL}>
      {children}
      <ArrowRight />
    </a>
  </Button>
);

const WebUploader = () => {
  const { copy, lang } = useWebUploaderCopy();
  const { hero, previews, faster, features, ftp, more, cta } = copy;
  const sora = useSoraHeadings();
  const desktop = shot(desktop1x, desktop2x, 1200, 750);
  const phone = shot(phone1x, phone2x, 390, 844);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{copy.seo.title}</title>
        <meta name="description" content={copy.seo.description} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:title" content={copy.seo.title} />
        <meta property="og:description" content={copy.seo.description} />
      </Helmet>
      <AppHeader variant="marketing" />

      <main>
        {/* Hero: the promise, the way in, and the real uploader mid-upload */}
        <section className="relative overflow-hidden bg-hawk-navy pb-16 pt-32 text-white md:pb-24">
          <div className="absolute inset-0 bg-radial-orange opacity-20" />
          <div className="container relative z-10 mx-auto px-4">
            <div className="mx-auto max-w-3xl text-center">
              <div className="mb-5 flex flex-wrap items-center justify-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-hawk-orange to-hawk-gold px-3 py-1 text-sm font-bold uppercase tracking-wide text-white shadow-glow">
                  <Sparkles size={14} aria-hidden="true" />
                  {hero.badge}
                </span>
                <span className="text-sm font-medium text-white/80">{hero.eyebrow}</span>
              </div>
              <h1 className={`mb-5 text-4xl font-display font-bold md:text-5xl${soraOnly(sora, "max-md:text-[2rem]")}`}>{hero.title}</h1>
              <p className="mx-auto mb-4 max-w-2xl text-lg text-white/80 md:text-xl">{hero.subtitle}</p>
              {/* Previews first (every plan, chosen per upload): its own line under the every-plan subline. */}
              <p className="mx-auto mb-8 max-w-2xl text-base font-medium text-white/90" data-testid="hero-plans">
                {hero.plans}
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <UploaderButton>{hero.ctaPrimary}</UploaderButton>
                <Button variant="heroOutline" size="lg" asChild>
                  <a href="#previews-first">
                    {hero.ctaSecondary}
                    <ArrowDown />
                  </a>
                </Button>
              </div>
              <p className="mt-5">
                <a href="#estimate" className="inline-flex items-center gap-1.5 font-semibold text-white underline-offset-4 hover:underline">
                  <Timer size={16} aria-hidden="true" />
                  {copy.estimate.heroLink}
                </a>
              </p>
            </div>

            {/* Phones: the uploader on a phone (the desktop screenshot would be too small to read). */}
            <div className="mx-auto mt-12 w-[250px] overflow-hidden rounded-[30px] border-[6px] border-zinc-900 bg-white shadow-2xl sm:hidden">
              <img src={phone.src} srcSet={phone.srcSet} width={phone.width} height={phone.height} alt={hero.phoneAlt} className="block h-auto w-full" />
            </div>
            <div className="relative mx-auto mt-12 hidden max-w-5xl sm:block md:mt-16">
              <div className="overflow-hidden rounded-xl border border-white/15 bg-white shadow-2xl">
                <div className="flex items-center gap-1.5 border-b border-black/5 bg-zinc-100 px-3 py-2" aria-hidden="true">
                  <span className="h-2.5 w-2.5 rounded-full bg-zinc-300" />
                  <span className="h-2.5 w-2.5 rounded-full bg-zinc-300" />
                  <span className="h-2.5 w-2.5 rounded-full bg-zinc-300" />
                  <span className="mx-auto truncate rounded bg-white px-3 py-0.5 text-[11px] text-zinc-500">upload.photohawk.com</span>
                </div>
                <img
                  src={desktop.src}
                  srcSet={desktop.srcSet}
                  width={desktop.width}
                  height={desktop.height}
                  alt={hero.screenshotAlt}
                  className="block h-auto w-full"
                />
              </div>
              <div className="absolute -bottom-10 -right-4 hidden w-[180px] overflow-hidden rounded-[28px] border-[6px] border-zinc-900 bg-white shadow-2xl md:block lg:-right-10 lg:w-[210px]">
                <img src={phone.src} srcSet={phone.srcSet} width={phone.width} height={phone.height} alt={hero.phoneAlt} className="block h-auto w-full" loading="lazy" />
              </div>
            </div>
          </div>
        </section>

        {/* Previews first */}
        <section id="previews-first" className="scroll-mt-24 py-20 md:py-24" aria-labelledby="previews-heading">
          <div className="container mx-auto grid max-w-6xl items-start gap-10 px-4 lg:grid-cols-[5fr_6fr] lg:gap-14">
            <div className="lg:sticky lg:top-28">
              <div className="mb-3 flex flex-wrap items-center gap-3">
                <h2 id="previews-heading" className="text-3xl font-display font-bold md:text-4xl">{previews.heading}</h2>
                <span className="rounded-full border border-hawk-orange/40 bg-hawk-orange/10 px-3 py-1 text-sm font-semibold text-hawk-orange" data-testid="previews-plans">
                  {previews.badge}
                </span>
              </div>
              <p className="mb-4 text-lg font-medium text-foreground/80">{previews.subtitle}</p>
              <p className="mb-6 text-muted-foreground">{previews.body}</p>
              <Link to={PREVIEW_FIRST_PATH} className="inline-flex items-center gap-1.5 font-semibold text-hawk-orange hover:underline">
                {previews.link}
                <ArrowRight size={16} />
              </Link>
            </div>
            <PreviewsFirstAnimation copy={previews} pause={copy.pause} play={copy.play} />
          </div>
        </section>

        {/* Why it's faster than FTP */}
        <section className="bg-secondary/30 py-20 md:py-24" aria-labelledby="faster-heading">
          <div className="container mx-auto max-w-6xl px-4">
            <div className="mx-auto mb-10 max-w-2xl text-center">
              <h2 id="faster-heading" className="mb-3 text-3xl font-display font-bold md:text-4xl">{faster.heading}</h2>
              <p className="text-muted-foreground">{faster.subtitle}</p>
            </div>
            <div className="grid items-start gap-10 lg:grid-cols-[6fr_5fr] lg:gap-14">
              <FasterThanFtpAnimation copy={faster} pause={copy.pause} play={copy.play} />
              <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
                {faster.points.map((p) => {
                  const Icon = POINT_ICONS[p.id] ?? Check;
                  return (
                    <li key={p.id} className="flex gap-4 rounded-2xl border border-border bg-card p-5">
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-hawk-orange/10">
                        <Icon size={18} className="text-hawk-orange" aria-hidden="true" />
                      </div>
                      <div>
                        <h3 className="mb-1 font-semibold">{p.title}</h3>
                        <p className="text-sm text-muted-foreground">{p.body}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </section>

        {/* How long will your upload take? (the estimator + one-tap speed test) */}
        <section id="estimate" className="scroll-mt-24 py-20 md:py-24" aria-labelledby="estimate-heading">
          <div className="container mx-auto max-w-6xl px-4">
            <div className="mx-auto mb-10 max-w-2xl text-center">
              <h2 id="estimate-heading" className="mb-3 text-3xl font-display font-bold md:text-4xl">{copy.estimate.heading}</h2>
              <p className="text-muted-foreground">{copy.estimate.subtitle}</p>
            </div>
            <UploadEstimator copy={copy.estimate} lang={lang} />
          </div>
        </section>

        {/* More that's new: real screenshots */}
        <section className="py-20 md:py-24" aria-labelledby="features-heading">
          <div className="container mx-auto max-w-6xl px-4">
            <div className="mx-auto mb-14 max-w-2xl text-center">
              <h2 id="features-heading" className="mb-3 text-3xl font-display font-bold md:text-4xl">{features.heading}</h2>
              <p className="text-muted-foreground">{features.subtitle}</p>
            </div>
            <div className="space-y-16 md:space-y-20">
              {features.items.map((f, i) => {
                const s = FEATURE_SHOTS[f.id];
                return (
                  <div key={f.id} className="grid items-center gap-8 md:grid-cols-2 md:gap-12">
                    <div className={i % 2 ? "md:order-2" : undefined}>
                      <h3 className="mb-2 font-display text-2xl font-bold">{f.title}</h3>
                      <p className="text-muted-foreground">{f.body}</p>
                    </div>
                    {s && (
                      <div className={`flex justify-center ${i % 2 ? "md:order-1" : ""}`}>
                        <img
                          src={s.src}
                          srcSet={s.srcSet}
                          width={s.width}
                          height={s.height}
                          alt={f.alt}
                          loading="lazy"
                          decoding="async"
                          className={`block h-auto border border-border bg-white shadow-lg ${s.phone ? "w-full max-w-[280px] rounded-[24px] border-[5px] border-zinc-900" : "w-full rounded-xl"} ${f.id === "status" ? "max-w-[400px]" : ""}`}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* What's changing with FTP on 1 November */}
        <section className="bg-secondary/30 py-20" aria-labelledby="ftp-heading">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-5xl rounded-2xl border border-hawk-gold/50 bg-card p-6 shadow-lg md:p-8">
              <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 id="ftp-heading" className="mb-1 font-display text-2xl font-bold md:text-3xl">{ftp.heading}</h2>
                  <p className="text-muted-foreground">{ftp.body}</p>
                </div>
                <span className="inline-flex flex-shrink-0 items-center gap-2 self-start rounded-full bg-hawk-orange/10 px-4 py-1.5 text-sm font-semibold text-hawk-orange">
                  <CalendarClock size={14} aria-hidden="true" />
                  {ftp.date}
                </span>
              </div>
              <ul className="grid gap-4 md:grid-cols-3">
                {ftp.points.map((p) => {
                  const Icon = FTP_ICONS[p.id] ?? Check;
                  return (
                    <li key={p.id} className="flex gap-4 rounded-xl border border-border p-4">
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-hawk-orange/10">
                        <Icon size={18} className="text-hawk-orange" aria-hidden="true" />
                      </div>
                      <div>
                        <h3 className="mb-1 font-semibold">{p.title}</h3>
                        <p className="text-sm text-muted-foreground">{p.body}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
              <Link to={ON_DEMAND_FTP_HELP_URL} className="mt-6 inline-flex items-center gap-1.5 font-semibold text-hawk-orange hover:underline">
                {ftp.helpLink}
                <ArrowRight size={15} aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>

        {/* More reading */}
        <section className="py-16" aria-label={more.previewFirstTitle}>
          <div className="container mx-auto max-w-3xl px-4">
            <Link
              to={PREVIEW_FIRST_PATH}
              className="group flex items-center gap-4 rounded-2xl border border-border bg-card p-5 shadow-sm transition-shadow hover:shadow-md"
            >
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-hawk-orange to-hawk-gold">
                <Globe className="text-white" size={22} aria-hidden="true" />
              </div>
              <div className="min-w-0 flex-1">
                <span className="block font-semibold group-hover:text-hawk-orange">{more.previewFirstTitle}</span>
                <span className="block text-sm text-muted-foreground">{more.previewFirstBody}</span>
              </div>
              <ArrowRight className="flex-shrink-0 text-hawk-orange" size={18} aria-hidden="true" />
            </Link>
          </div>
        </section>

        {/* CTA */}
        <section className="relative overflow-hidden bg-hawk-navy py-20 text-white">
          <div className="absolute inset-0 bg-radial-orange opacity-20" />
          <div className="container relative z-10 mx-auto max-w-3xl px-4 text-center">
            <UploadCloud className="mx-auto mb-4 text-hawk-orange" size={32} aria-hidden="true" />
            <h2 className="mb-3 text-3xl font-display font-bold md:text-4xl">{cta.heading}</h2>
            <p className="mb-8 text-white/80">{cta.body}</p>
            <UploaderButton>{cta.button}</UploaderButton>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default WebUploader;
